import { GlobeSimpleIcon, UsersThreeIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import type { SchoolClass } from "p6m-p6u";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import { PhosphorIcon } from "../../basic/phosphorIcon/PhosphorIcon";
import { isPrivateSchoolClass } from "../../utils/schoolClassUtils";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";

interface SchoolClassCardProps {
    classItem: SchoolClass;
    onClick: () => void;
}

const SchoolClassCard = (props: SchoolClassCardProps) => {
    const t = useT();
    const t_public = t("Public", { _tags: "SchoolClasses, SchoolClassCard" });
    const t_private = t("Private", { _tags: "SchoolClasses, SchoolClassCard" });

    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const isPublic = !isPrivateSchoolClass(props.classItem.classVisibility);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            props.onClick();
        }
    };

    const renderVisibilityIcon = () => {
        if (isPublic) {
            return (
                <PhosphorIcon
                    phosphorIcon={GlobeSimpleIcon}
                    color="text-p6-grey-mid"
                    fontSize="text-xl"
                />
            );
        }

        return (
            <PhaseSixIcon
                name="lock"
                className="text-xxl text-p6-grey-mid"
            />
        );
    };

    const renderClassIcon = () => {
        return (
            <div
                className={`flex items-center justify-center ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "w-[50px] h-[50px]",
                    "w-[32px] h-[32px]"
                )}`}
            >
                <PhosphorIcon
                    phosphorIcon={UsersThreeIcon}
                    color="text-p6-grey-mid"
                    fontSize={getStyleBasedOnBreakpoint(Breakpoint.MD, "text-[50px]", "text-xxl")}
                />
            </div>
        );
    };

    const renderClassInfo = () => {
        return (
            <div className="flex flex-col gap-xxs flex-1 min-w-0">
                <p
                    className={`font-semibold text-p6-grey-dark text-l ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "truncate",
                        "break-words"
                    )}`}
                >
                    {props.classItem.name}
                </p>

                <div className="flex items-center gap-xxs">
                    <div className="flex items-center justify-center w-[32px] h-[32px]">{renderVisibilityIcon()}</div>
                    <p className="text-m text-p6-grey-mid">{isPublic ? t_public : t_private}</p>
                </div>
            </div>
        );
    };

    const renderChevron = () => {
        return (
            <div className="flex items-center justify-center min-w-[44px] min-h-[44px]">
                <PhaseSixIcon
                    name="chevron-right"
                    className="text-xl text-p6-grey-dark"
                />
            </div>
        );
    };

    return (
        <div
            role="button"
            tabIndex={0}
            data-testid={`school-class-card-${props.classItem.token}`}
            data-visibility={props.classItem.classVisibility}
            onClick={props.onClick}
            onKeyDown={handleKeyDown}
            aria-label={`${props.classItem.name}, ${isPublic ? t_public : t_private}`}
            className={`flex items-center justify-between bg-white hover:bg-background-light transition-colors border border-p6-grey-lightest rounded-medium cursor-pointer ${getStyleBasedOnBreakpoint(
                Breakpoint.MD,
                "p-m gap-m",
                "p-s gap-s"
            )}`}
        >
            <div className={`flex items-center flex-1 ${getStyleBasedOnBreakpoint(Breakpoint.MD, "gap-m", "gap-s")}`}>
                {renderClassIcon()}
                {renderClassInfo()}
            </div>

            {renderChevron()}
        </div>
    );
};

export default SchoolClassCard;
